import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useState, } from 'react';
import { cn } from '../../lib/cn';
import ExpandableItem from './ExpandableItem';
const DEFAULT_POINTER_POSITION = '50%';
const supportsFineHoverPointer = () => {
    if (typeof window === 'undefined')
        return false;
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
};
const prefersReducedMotion = () => {
    if (typeof window === 'undefined')
        return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};
const Card = forwardRef(({ children, className, closeRequestKey = 0, expansionPreset, interactive = true, isExpanded = false, onOverlayExitComplete, overlay, opened = false, onAnimationEnd, onPointerEnter, onPointerLeave, onPointerMove, style, ...props }, ref) => {
    const previewInteractive = interactive && !opened;
    const hoverCapablePointer = previewInteractive && supportsFineHoverPointer();
    const reducedMotion = previewInteractive && prefersReducedMotion();
    const [hoverPhase, setHoverPhase] = useState('idle');
    useEffect(() => {
        if (!previewInteractive && hoverPhase !== 'idle') {
            setHoverPhase('idle');
        }
    }, [hoverPhase, previewInteractive]);
    const handlePointerEnter = (event) => {
        onPointerEnter?.(event);
        if (!hoverCapablePointer)
            return;
        setHoverPhase(reducedMotion ? 'holding' : 'animating');
    };
    const handlePointerMove = (event) => {
        onPointerMove?.(event);
        if (!hoverCapablePointer)
            return;
        const bounds = event.currentTarget.getBoundingClientRect();
        const x = `${event.clientX - bounds.left}px`;
        const y = `${event.clientY - bounds.top}px`;
        event.currentTarget.style.setProperty('--mx', x);
        event.currentTarget.style.setProperty('--my', y);
    };
    const handlePointerLeave = (event) => {
        onPointerLeave?.(event);
        if (!hoverCapablePointer)
            return;
        setHoverPhase('idle');
        event.currentTarget.style.setProperty('--mx', DEFAULT_POINTER_POSITION);
        event.currentTarget.style.setProperty('--my', DEFAULT_POINTER_POSITION);
    };
    const handleKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            props.onClick?.(event);
        }
    };
    const handleAnimationEnd = (event) => {
        onAnimationEnd?.(event);
        if (!hoverCapablePointer || reducedMotion)
            return;
        if (!(event.target instanceof HTMLElement))
            return;
        if (event.target.dataset.goldPulseText !== 'true')
            return;
        setHoverPhase('holding');
    };
    return (_jsxs(ExpandableItem, { ref: ref, role: previewInteractive ? 'button' : undefined, tabIndex: previewInteractive ? 0 : undefined, "aria-expanded": previewInteractive ? isExpanded : undefined, onKeyDown: previewInteractive ? handleKeyDown : undefined, closeRequestKey: closeRequestKey, "data-hover-phase": hoverPhase, "data-opened": opened ? 'true' : 'false', expanded: opened, className: cn('relative flex h-full w-full overflow-hidden rounded-lg border border-border-subtle bg-gradient-to-br from-bg-card to-bg-card-deep shadow-[var(--shadow-ambient)]', interactive &&
            'group/card isolate transition-[border-color,box-shadow] duration-[400ms] ease-[var(--ease-standard)] motion-reduce:transition-none', previewInteractive &&
            'cursor-pointer pointer-fine:hover:border-border-hover pointer-fine:hover:shadow-[var(--shadow-card-hover)] pointer-coarse:active:border-border-accent pointer-coarse:active:shadow-[var(--shadow-touch-feedback)]', previewInteractive &&
            'focus-visible:outline-none focus-visible:border-border-accent focus-visible:shadow-focus-ring', className, opened &&
            'fixed z-20 items-stretch justify-start overflow-hidden border-transparent bg-bg-expanded bg-none shadow-[var(--shadow-elevated)]'), onOverlayExitComplete: onOverlayExitComplete, onAnimationEnd: handleAnimationEnd, onPointerEnter: handlePointerEnter, onPointerLeave: handlePointerLeave, onPointerMove: handlePointerMove, overlay: overlay, preset: expansionPreset, style: style, ...props, children: [interactive ? (_jsxs(_Fragment, { children: [_jsx("div", { "aria-hidden": "true", className: "card-hover-corner-bleed pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 transition-opacity duration-[500ms] ease-[var(--ease-standard)] motion-safe:pointer-fine:group-hover/card:opacity-100 motion-reduce:opacity-0 motion-reduce:transition-none group-data-[opened=true]/card:!opacity-0" }), _jsx("div", { "aria-hidden": "true", className: "card-hover-radial pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 transition-opacity duration-[500ms] ease-[var(--ease-standard)] motion-safe:pointer-fine:group-hover/card:opacity-100 motion-reduce:opacity-0 motion-reduce:transition-none group-data-[opened=true]/card:!opacity-0" }), _jsx("div", { "aria-hidden": "true", className: "card-hover-sheen pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 transition-opacity duration-[500ms] ease-[var(--ease-standard)] motion-safe:pointer-fine:group-hover/card:opacity-100 motion-reduce:opacity-0 motion-reduce:transition-none group-data-[opened=true]/card:!opacity-0" })] })) : null, _jsx("div", { className: cn('relative z-10 h-full w-full', interactive &&
                    '[transform:translateZ(0)] transition-transform duration-[400ms] ease-[var(--ease-standard)] motion-safe:pointer-fine:group-hover/card:[transform:translateZ(0)_scale(1.02)] motion-reduce:[transform:none] motion-reduce:transition-none group-data-[opened=true]/card:![transform:translateZ(0)]'), children: !opened ? children : null })] }));
});
Card.displayName = 'Card';
export default Card;
